import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Restore the window scroll position when the user goes back/forward.
 *
 * React Router keeps a unique `location.key` per history entry, so the scroll
 * offsets are stored per entry instead of per URL: the same list opened from
 * two different places gets two independent positions. PUSH/REPLACE entries
 * still start at the top.
 *
 * Pages fetch their data after mounting, so the document may be too short when
 * the restore starts; the loop keeps applying the saved offset for a few
 * seconds while content (or the next page of it) arrives. Any real user input
 * aborts the loop so it never fights a manual scroll.
 */
const RESTORE_TIMEOUT_MS = 4000;

const scrollPositions = new Map<string, number>();

const ScrollRestoration: React.FC = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const restoreToken = useRef(0);

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  useEffect(() => {
    const key = location.key;
    let frame = 0;

    const save = () => {
      frame = 0;
      scrollPositions.set(key, window.scrollY);
    };
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(save);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
      scrollPositions.set(key, window.scrollY);
    };
  }, [location.key]);

  useEffect(() => {
    const key = location.key;
    const token = ++restoreToken.current;

    if (navigationType !== "POP") {
      window.scrollTo(0, 0);
      return;
    }

    const target = scrollPositions.get(key);
    if (target === undefined) return;

    const startedAt = performance.now();
    let frame = 0;

    const stop = () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = 0;
      restoreToken.current += 1;
    };

    const attempt = () => {
      if (token !== restoreToken.current) return;

      const maxScroll = Math.max(
        0,
        document.documentElement.scrollHeight - window.innerHeight,
      );
      const next = Math.min(target, maxScroll);
      window.scrollTo(0, next);

      const reached = Math.abs(window.scrollY - target) <= 2;
      const timedOut = performance.now() - startedAt > RESTORE_TIMEOUT_MS;
      if (reached || timedOut) return;
      frame = window.requestAnimationFrame(attempt);
    };

    frame = window.requestAnimationFrame(attempt);
    window.addEventListener("wheel", stop, { passive: true });
    window.addEventListener("touchstart", stop, { passive: true });
    window.addEventListener("keydown", stop);
    window.addEventListener("pointerdown", stop);

    return () => {
      stop();
      window.removeEventListener("wheel", stop);
      window.removeEventListener("touchstart", stop);
      window.removeEventListener("keydown", stop);
      window.removeEventListener("pointerdown", stop);
    };
  }, [location.key, navigationType]);

  return null;
};

export default ScrollRestoration;
